import { useCallback, useEffect, useRef, useState } from 'react';
import { syncMetaNow } from '../../services/metaAdsService';
import {
  getMetaErrorMessage,
  needsMetaReconnect,
  readMetaErrorCode,
  readMetaRetryAfterSeconds,
} from '../metaAdsConfig';
import { normalizeMetaSyncProgress } from '../metaAdsConnection';

/** Texto mientras corre la primera carga y la API todavía no mandó `sync_progress`. */
export const META_INITIAL_LOAD_MESSAGE = 'Estamos trayendo los últimos 90 días';

/** Tope de vueltas automáticas seguidas (contrato Ajuste 7: «~20 vueltas»). */
export const META_SYNC_MAX_AUTO_RUNS = 20;
/** Vueltas automáticas seguidas sin avance de `days_loaded` antes de pausar (APIBOT). */
export const META_SYNC_MAX_STALLED_RUNS = 3;
/** Esperas más largas que esto (p. ej. META_RATE_LIMITED) no dejan timer: se pausa con botón manual. */
export const META_SYNC_MAX_AUTO_WAIT_MS = 120 * 1000;
/** API vieja (sin `should_auto_sync`): al abrir el panel, sync sola si el último dato tiene más de esto. */
export const META_SYNC_STALE_MS = 30 * 60 * 1000;
/** Ajuste 11: como mucho un chequeo de `getmetaconnection` cada 60 s al volver el foco / la pestaña. */
export const META_SYNC_FOCUS_THROTTLE_MS = 60 * 1000;
/** Espera si la API pide seguir sin `retry_after_s` ni `next_sync_allowed_at`. */
const DEFAULT_RETRY_MS = 15000;
const MIN_RETRY_MS = 1000;
const MAX_RETRY_MS = 10 * 60 * 1000;

/** `sync_progress` de la respuesta: en `data` (200) o junto a `code` (429 / 409). */
export function readMetaSyncProgress(res) {
  return normalizeMetaSyncProgress(res?.data?.sync_progress ?? res?.sync_progress ?? null);
}

/**
 * Cuánto esperar antes de la próxima vuelta: `retry_after_s` y, si es más tarde,
 * `next_sync_allowed_at`. Entre 1 s y 10 min.
 */
export function metaSyncRetryDelayMs(progress, now = Date.now()) {
  let ms = progress?.retryAfterS != null ? progress.retryAfterS * 1000 : null;
  const allowedAt = progress?.nextSyncAllowedAt ? Date.parse(progress.nextSyncAllowedAt) : NaN;
  if (Number.isFinite(allowedAt)) ms = Math.max(ms ?? 0, allowedAt - now);
  if (ms == null) ms = DEFAULT_RETRY_MS;
  return Math.min(MAX_RETRY_MS, Math.max(MIN_RETRY_MS, ms));
}

/**
 * Medida de avance de la carga: `days` = el mayor entre `days_loaded` global (la API
 * lo da como suma de las cuentas) y la suma de `accounts[].days_loaded` (por si uno de
 * los dos falta o viene atrasado); `pending` = `accounts_pending`. `optional_pending`
 * no cuenta.
 */
export function metaSyncProgressMetric(progress) {
  if (!progress) return { days: null, pending: null };
  const perAccount = (progress.accounts || []).map((a) => a.daysLoaded).filter((n) => n != null);
  const sum = perAccount.length ? perAccount.reduce((acc, n) => acc + n, 0) : null;
  const candidates = [progress.daysLoaded, sum].filter((n) => n != null);
  return {
    days: candidates.length ? Math.max(...candidates) : null,
    pending: progress.accountsPending ?? null,
  };
}

/** ¿Avanzó? Más días cargados o menos cuentas pendientes. Sin datos para comparar → no. */
export function metaSyncAdvanced(prev, next) {
  if (!prev || !next) return false;
  if (next.days != null && prev.days != null && next.days > prev.days) return true;
  return next.pending != null && prev.pending != null && next.pending < prev.pending;
}

/**
 * ¿Sync sola al abrir el panel? Solo con `can_sync`, sin una sync corriendo ni una
 * carga en curso (de eso se ocupa la continuación), sin `next_sync_allowed_at` en el
 * futuro y con `last_sync_at` de hace más de 30 min (o null: hay cuentas seguidas,
 * si no `sync_progress` vendría null).
 */
export function metaSyncIsStaleOnOpen(progress, now = Date.now()) {
  if (!progress || !progress.canSync || progress.syncRunning || progress.shouldContinue) return false;
  if (progress.state === 'initial_load' || progress.state === 'updating') return false;
  const allowedAt = progress.nextSyncAllowedAt ? Date.parse(progress.nextSyncAllowedAt) : NaN;
  if (Number.isFinite(allowedAt) && allowedAt > now) return false;
  if (!progress.lastSyncAt) return true;
  const last = Date.parse(progress.lastSyncAt);
  return !Number.isFinite(last) || now - last > META_SYNC_STALE_MS;
}

/**
 * Ajuste 11: ¿sync sola en silencio? Lo decide el servidor (`should_auto_sync`, con su
 * reloj; el Front no compara horas). Conservador: además pide `can_sync` y que no haya
 * una sync corriendo. API vieja (sin el campo): al abrir, la regla de 30 min de antes;
 * en foco, nada (antes no existía).
 *
 * @param {object|null} progress `sync_progress` normalizado.
 * @param {{ onOpen?: boolean, now?: number }} [options]
 */
export function metaSyncShouldAutoSync(progress, { onOpen = false, now = Date.now() } = {}) {
  if (!progress) return false;
  if (typeof progress.shouldAutoSync === 'boolean') {
    return progress.shouldAutoSync && progress.canSync && !progress.syncRunning;
  }
  return onOpen ? metaSyncIsStaleOnOpen(progress, now) : false;
}

/** ¿La API pide otra vuelta y se puede? (`should_continue` + `can_sync`). */
export const metaSyncShouldContinue = (progress) => Boolean(progress?.shouldContinue && progress.canSync);

/**
 * Resultado de una sync para mostrar. Puro (testeable sin hook).
 *
 * Ajuste 7: `partial` ya no sale por tiempo; un `partial` sin `error_code` (o con
 * el viejo META_SYNC_TIME_BUDGET) cuenta como `ok`: lo que falta lo dice
 * `sync_progress`. `continuing` = la API pide otra vuelta (también en 429 / 409).
 *
 * @param {object} res Envelope de `POST /syncmetanow`.
 * @returns {{ kind: 'ok'|'partial'|'error'|'throttled'|'in_progress'|'reconnect'|'unknown',
 *   message: string|null, retryAfterS?: number, progress: object|null, continuing: boolean }}
 */
export function readMetaSyncOutcome(res) {
  const progress = readMetaSyncProgress(res);
  const base = (kind, message, extra = {}) => ({ kind, message, progress, continuing: false, ...extra });
  const code = readMetaErrorCode(res);
  if (res?._httpOk) {
    const data = res.data || {};
    const cont = metaSyncShouldContinue(progress);
    if (data.status === 'ok') return base('ok', null, { continuing: cont });
    if (data.status === 'partial' && (!data.error_code || data.error_code === 'META_SYNC_TIME_BUDGET')) {
      return base('ok', null, { continuing: cont });
    }
    const fallback = data.status === 'partial'
      ? 'Algunas cuentas quedaron para la próxima actualización.'
      : 'No pudimos traer los datos de Meta. Mostramos los últimos que tenemos.';
    const message = data.error_code ? getMetaErrorMessage({ code: data.error_code }, fallback) : fallback;
    // `partial` real: la API puede pedir seguir con las cuentas que faltan.
    return base(data.status === 'partial' ? 'partial' : 'error', message, { continuing: data.status === 'partial' && cont });
  }
  if (code === 'META_SYNC_THROTTLED' || code === 'META_RATE_LIMITED' || res?._httpStatus === 429) {
    return base('throttled', getMetaErrorMessage(res), {
      retryAfterS: progress?.retryAfterS ?? readMetaRetryAfterSeconds(res),
      continuing: metaSyncShouldContinue(progress),
    });
  }
  if (code === 'META_SYNC_IN_PROGRESS') {
    return base('in_progress', getMetaErrorMessage(res), { continuing: metaSyncShouldContinue(progress) });
  }
  if (needsMetaReconnect(res)) return base('reconnect', getMetaErrorMessage(res));
  // Red caída / timeout: la sync puede seguir corriendo en la API.
  if (res?._httpStatus == null) {
    return base('unknown', 'No pudimos confirmar si terminó. Revisamos el estado en un momento.');
  }
  return base('error', getMetaErrorMessage(res));
}

/**
 * «Traer datos» (`POST /syncmetanow` con `{}`, solo dueño) + continuación sola
 * (Ajuste 7): mientras `sync_progress.should_continue` (y `can_sync`), espera
 * `retry_after_s` (o hasta `next_sync_allowed_at`) y vuelve a llamar. También
 * arranca solo si `getmetaconnection` ya trae `should_continue` (`progress`).
 *
 * Corta: al desmontar, con `autoContinue` en false (desconectado / sin permiso),
 * ante cualquier otro error, tras META_SYNC_MAX_AUTO_RUNS vueltas, tras
 * META_SYNC_MAX_STALLED_RUNS vueltas automáticas (200) seguidas sin avance, o si la
 * espera pedida pasa META_SYNC_MAX_AUTO_WAIT_MS. En esos tres últimos casos deja
 * `paused` ({ reason, message }) para que la UI ofrezca «Seguir cargando»
 * (`resume`), que reinicia las dos cuentas. Nunca dos llamadas a la vez.
 * `onSettled` corre después de cada respuesta (refrescar).
 *
 * Ante 429 sin `should_continue` deja `retryInS` en cuenta regresiva.
 *
 * Ajuste 11: al abrir y al volver el foco / la pestaña visible (como mucho cada
 * META_SYNC_FOCUS_THROTTLE_MS), `refreshConnection()` trae `sync_progress` fresco y, si
 * `should_auto_sync`, corre una sync silenciosa (una por respuesta). Un 429 / 409 no
 * se reintenta: espera al próximo foco.
 *
 * @param {{ onSettled?: (outcome: object) => void, progress?: object|null, autoContinue?: boolean,
 *   refreshConnection?: (() => Promise<object|undefined>)|null }} [options]
 */
export function useMetaSync({
  onSettled, progress: connectionProgress = null, autoContinue = false, refreshConnection = null,
} = {}) {
  const [running, setRunning] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [progress, setProgress] = useState(null);
  const [paused, setPaused] = useState(null);
  const [retryAt, setRetryAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const timerRef = useRef(null);
  const autoRunsRef = useRef(0);
  const stalledRunsRef = useRef(0);
  const lastMetricRef = useRef(null);
  // Cortado por error, tope, estancamiento o espera larga: no se reanuda solo (ni con
  // la conexión refrescada) hasta un pedido a mano o hasta que la API diga que terminó.
  const haltedRef = useRef(false);
  const autoRef = useRef(autoContinue);
  autoRef.current = autoContinue;
  const runRef = useRef(null);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const resetCounters = useCallback(() => {
    autoRunsRef.current = 0;
    stalledRunsRef.current = 0;
    lastMetricRef.current = null;
    haltedRef.current = false;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  useEffect(() => {
    if (!retryAt) return undefined;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= retryAt) setRetryAt(null);
    }, 1000);
    return () => clearInterval(id);
  }, [retryAt]);

  const retryInS = retryAt ? Math.max(0, Math.ceil((retryAt - now) / 1000)) : 0;

  const pause = useCallback((reason, message, waitMs = 0) => {
    haltedRef.current = true;
    clearTimer();
    if (!mountedRef.current) return;
    setPaused({ reason, message: message || null });
    if (waitMs > 0) {
      const t = Date.now();
      setNow(t);
      setRetryAt(t + waitMs);
    }
  }, [clearTimer]);

  /** Agenda la próxima vuelta si corresponde. Devuelve si quedó agendada. */
  const schedule = useCallback((p, message = null) => {
    if (!autoRef.current || !mountedRef.current || !metaSyncShouldContinue(p)) return false;
    if (timerRef.current) return true;
    if (busyRef.current || haltedRef.current) return false;
    if (autoRunsRef.current >= META_SYNC_MAX_AUTO_RUNS) {
      pause('max_runs', p.message);
      return false;
    }
    const delay = metaSyncRetryDelayMs(p);
    const allowedAt = p.nextSyncAllowedAt ? Date.parse(p.nextSyncAllowedAt) : NaN;
    const wait = Math.max(
      p.retryAfterS != null ? p.retryAfterS * 1000 : 0,
      Number.isFinite(allowedAt) ? allowedAt - Date.now() : 0,
    );
    if (wait > META_SYNC_MAX_AUTO_WAIT_MS) {
      // Espera larga (p. ej. META_RATE_LIMITED): sin timer; botón manual cuando se pueda.
      pause('long_wait', message || p.message, wait);
      return false;
    }
    if (lastMetricRef.current == null) lastMetricRef.current = metaSyncProgressMetric(p);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (!mountedRef.current || !autoRef.current) return;
      autoRunsRef.current += 1;
      runRef.current?.({ auto: true });
    }, delay);
    return true;
  }, [pause]);

  // `silent`: la sync sola al abrir el panel. Arranca como un pedido nuevo (reinicia
  // las cuentas) pero sin avisos: ni toast de ok ni aviso de 429 / 409.
  const run = useCallback(async ({ initial = false, auto = false, silent = false } = {}) => {
    if (busyRef.current) return null;
    if (!auto && retryAt && Date.now() < retryAt) return null;
    if (!auto) {
      // Pedido a mano: reinicia las cuentas (tope y estancamiento) y saca la espera agendada.
      clearTimer();
      resetCounters();
      setPaused(null);
    }
    busyRef.current = true;
    setRunning(true);
    if (!auto) setInitialLoad(initial);
    setOutcome(null);
    const res = await syncMetaNow({});
    const result = { ...readMetaSyncOutcome(res), auto: auto || silent, silent };
    busyRef.current = false;
    if (!mountedRef.current) return result;
    setRunning(false);
    setOutcome(result);
    if (result.progress) setProgress(result.progress);
    if (result.kind === 'throttled' && !result.continuing) {
      const t = Date.now();
      setNow(t);
      setRetryAt(t + result.retryAfterS * 1000);
    }
    onSettledRef.current?.(result);

    // Estancamiento: solo cuentan las vueltas automáticas que respondieron 200 (un 409
    // «en curso» o un 429 no son falta de avance).
    let stalled = false;
    if (result.continuing && res?._httpOk && result.progress) {
      const metric = metaSyncProgressMetric(result.progress);
      if (auto) {
        stalledRunsRef.current = metaSyncAdvanced(lastMetricRef.current, metric) ? 0 : stalledRunsRef.current + 1;
        stalled = stalledRunsRef.current >= META_SYNC_MAX_STALLED_RUNS;
      }
      lastMetricRef.current = metric;
    }
    if (stalled) {
      pause('stalled', result.progress.message);
      setInitialLoad(false);
      return result;
    }

    // 429 / 409: el mensaje de la respuesta (por `code`) es el que explica la espera.
    const waitMessage = result.kind === 'throttled' || result.kind === 'in_progress' ? result.message : null;
    const scheduled = result.continuing && schedule(result.progress, waitMessage);
    if (!scheduled) {
      setInitialLoad(false);
      if (result.kind === 'ok' && !result.continuing) {
        resetCounters();
      } else if (!result.continuing) {
        // Otro error (o 429 / 409 sin `should_continue`): se corta la continuación.
        haltedRef.current = true;
      }
    }
    return result;
  }, [retryAt, clearTimer, resetCounters, schedule, pause]);
  runRef.current = run;

  // La conexión (getmetaconnection) ya trae `should_continue`: seguir solo.
  // Sin permiso / desconectado → se cancela la espera.
  useEffect(() => {
    if (!autoContinue) {
      clearTimer();
      return;
    }
    if (!connectionProgress) return;
    if (!connectionProgress.shouldContinue && !busyRef.current && !timerRef.current) {
      // La API dice que terminó: la próxima carga arranca de cero.
      resetCounters();
      setPaused(null);
      return;
    }
    schedule(connectionProgress);
  }, [autoContinue, connectionProgress, schedule, clearTimer, resetCounters]);

  // `next_sync_allowed_at` de la conexión en el futuro: cuenta regresiva en `retryInS`
  // (el ícono «Actualizar datos» y «Seguir cargando» quedan deshabilitados hasta ahí).
  const nextAllowedAt = connectionProgress?.nextSyncAllowedAt ? Date.parse(connectionProgress.nextSyncAllowedAt) : NaN;
  useEffect(() => {
    if (!Number.isFinite(nextAllowedAt) || nextAllowedAt <= Date.now()) return;
    setNow(Date.now());
    setRetryAt((prev) => (prev && prev >= nextAllowedAt ? prev : nextAllowedAt));
  }, [nextAllowedAt]);

  // Sync sola al abrir (una vez por montaje del panel; el ref sobrevive al doble
  // montaje de StrictMode). Se decide con el primer `sync_progress` que llega.
  const openCheckedRef = useRef(false);
  const lastFocusCheckRef = useRef(0);
  useEffect(() => {
    if (openCheckedRef.current || !autoContinue || !connectionProgress) return;
    openCheckedRef.current = true;
    // El chequeo de apertura cuenta para el throttle del foco.
    lastFocusCheckRef.current = Date.now();
    if (busyRef.current || timerRef.current || !metaSyncShouldAutoSync(connectionProgress, { onOpen: true })) return;
    runRef.current?.({ silent: true });
  }, [autoContinue, connectionProgress]);

  // Ajuste 11: foco de ventana / pestaña visible (mientras el panel está montado).
  const refreshRef = useRef(refreshConnection);
  refreshRef.current = refreshConnection;
  const hasRefresh = Boolean(refreshConnection);
  useEffect(() => {
    if (!autoContinue || !hasRefresh || typeof window === 'undefined') return undefined;
    let checking = false;
    const check = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const t = Date.now();
      if (checking || t - lastFocusCheckRef.current < META_SYNC_FOCUS_THROTTLE_MS) return;
      // Con una sync corriendo o una vuelta agendada ya se refresca solo.
      if (busyRef.current || timerRef.current) return;
      checking = true;
      lastFocusCheckRef.current = t;
      let conn = null;
      try {
        conn = await refreshRef.current?.();
      } finally {
        checking = false;
      }
      if (!mountedRef.current || !autoRef.current || busyRef.current || timerRef.current) return;
      if (metaSyncShouldAutoSync(conn?.syncProgress || null)) runRef.current?.({ silent: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [autoContinue, hasRefresh]);

  const clearOutcome = useCallback(() => setOutcome(null), []);
  /** «Seguir cargando» (dueño): pedido a mano, reinicia tope y estancamiento. */
  const resume = useCallback(() => runRef.current?.({ auto: false }), []);

  return { running, initialLoad, outcome, progress, paused, retryInS, run, resume, clearOutcome };
}
